import React, { useEffect, useMemo, useState } from 'react';
import { AsyncConnector, DataGrid, DataGridHeader, Loader } from '@axiom/ui';
import type { BarredLocation } from '@axiom/validation';

import type {
  CandidateDetail,
  MatchingCandidate,
  ReadCandidatesPayload,
} from '../../api/chat-api';
import { ChatApi } from '../../api/chat-api';
import { MatchGridColumnConfigUtil } from '../../utils/match-grid-column-config-util';
import { MatchGridUtil } from '../../utils/match-grid-util';
import type { MatchGridSavedColumns } from '../../../schema/spark/match-grid-column-config-api-schema';
import type { MatchGridColumnConfigItem } from '../../types/match-grid.types';
import { MatchGridColumn } from '../../enums/match-grid-column';

import type { MatchGridColumnConfigProps } from './MatchGridColumnConfig';
import { MatchGridColumnConfig } from './MatchGridColumnConfig';
import { MatchGridAvailability } from './MatchGridAvailability';
import { MatchGridCandidateName } from './MatchGridCandidateName';
import { MatchGridDownloadBio } from './MatchGridDownloadBio';
import { MatchGridFeedbackResponses } from './MatchGridFeedbackResponses';
import { MatchGridProfileStatus } from './MatchGridProfileStatus';
import { MatchGridTalentEmail } from './MatchGridTalentEmail';
import { MatchGridTalentFlag } from './MatchGridTalentFlag';
import { MatchGridTalentFlagHeader } from './MatchGridTalentFlagHeader';
import { useMatchGridColumns } from './useMatchGridColumns';

/*
  **The grid's own file is the way into this folder.** Anything outside it may import only
  this one, so what the rest of the app is allowed to know about the grid's columns is named
  here rather than reached for inside.
*/
export { DEFAULT_MATCH_GRID_COLUMNS } from './match-grid-columns.const';

const PAGE_SIZE = 30;

// One reference, so the default does not re-key the lookup below on every render.
const NO_BARRED_LOCATIONS: BarredLocation[] = [];

const { NOT_YET } = MatchGridUtil;

const MatchGridHeaderUtil = {
  renderColumnConfig({
    columns,
    onClose,
    onOpen,
    onSave,
    showModal,
  }: MatchGridColumnConfigProps) {
    return () => (
      <MatchGridColumnConfig
        columns={columns}
        onClose={onClose}
        onOpen={onOpen}
        onSave={onSave}
        showModal={showModal}
      />
    );
  },
};

export type MatchGridProps = {
  name: string;
  isPeeking: boolean;
  /** Every barred location there is, to put names to the ids a candidate carries. */
  barredLocations?: BarredLocation[];
  matchingCandidates: MatchingCandidate[];
  /** True once this reader has a configuration to replace - see `ChatApi.readColumnConfig`. */
  hasSavedColumns?: boolean;
  savedColumns?: MatchGridSavedColumns | null;
  showColumnConfig?: boolean;
};

export const MatchGrid = ({
  name,
  isPeeking,
  barredLocations = NO_BARRED_LOCATIONS,
  hasSavedColumns = false,
  matchingCandidates,
  savedColumns = null,
  showColumnConfig = false,
}: MatchGridProps) => {
  const [explanationsById, setExplanationsById] = useState(
    () =>
      new Map<
        MatchingCandidate['candidateId'],
        MatchingCandidate['matchExplanation']
      >()
  );

  const matchedIdsKey = matchingCandidates
    .map(candidate => candidate.candidateId)
    .filter(Boolean)
    .join(',');

  const [candidateIdsKey, setCandidateIdsKey] = useState<string | null>(null);

  const [showSettings, setShowSettings] = useState(false);

  const { columns, isSaving, reorderColumns, saveColumns } =
    useMatchGridColumns({
      hasSavedColumns,
      openSettings: () => {
        setShowSettings(true);
      },
      savedColumns,
    });

  const submitSettings = (nextColumns: MatchGridColumnConfigItem[]) => {
    setShowSettings(false);
    void saveColumns(nextColumns);
  };

  const barredLocationNamesById = useMemo(
    () =>
      new Map(
        barredLocations.flatMap(location =>
          location.id && location.name
            ? [[location.id, location.name] as const]
            : []
        )
      ),
    [barredLocations]
  );

  const shownColumns = useMemo(() => {
    const shown: Record<string, boolean | string> = {};

    columns.forEach(item => {
      Object.entries(item).forEach(([columnId, value]) => {
        shown[columnId] = value;
      });
    });

    return shown;
  }, [columns]);
  const isShown = (columnId: string) => shownColumns[columnId] !== false;

  const orderedColumnIds = useMemo(
    () =>
      columns.flatMap(
        item => MatchGridColumnConfigUtil.columnIdOf({ item }) ?? []
      ),
    [columns]
  );

  useEffect(() => {
    if (!matchedIdsKey) return;

    setCandidateIdsKey(matchedIdsKey);
    setExplanationsById(
      new Map(
        matchingCandidates.map(candidate => [
          candidate.candidateId,
          candidate.matchExplanation,
        ])
      )
    );
  }, [matchedIdsKey, matchingCandidates]);

  const candidateIds = useMemo(
    () => (candidateIdsKey ? candidateIdsKey.split(',') : null),
    [candidateIdsKey]
  );

  const gridConnector = useMemo(
    () =>
      candidateIds
        ? new AsyncConnector<ReadCandidatesPayload>({
            // Never `[]`: the service reads it as no filter and returns the first 30 in the index.
            getData: () => ChatApi.readCandidates({ candidateIds }),
            pageSize: PAGE_SIZE,
            parseData: results => ({
              currentPage: 1,
              data: results.data,
              totalPages: results.data.length > 0 ? 1 : 0,
            }),
          })
        : undefined,
    [candidateIds]
  );

  const configurableHeaders: Record<string, React.ReactNode> = {
    [MatchGridColumn.RANK]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.RANK}
        name={MatchGridColumn.RANK}
        displayName="Rank"
        width={72}
        cellRender={({ node }) =>
          node.rowIndex === null ? NOT_YET : node.rowIndex + 1
        }
      />
    ),
    [MatchGridColumn.TALENT_NAME]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.TALENT_NAME}
        name={MatchGridColumn.TALENT_NAME}
        displayName="Name"
        cellRender={MatchGridCandidateName}
      />
    ),
    [MatchGridColumn.PROFILE_STATUS]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.PROFILE_STATUS}
        name={MatchGridColumn.PROFILE_STATUS}
        displayName="Profile Status"
        width={208}
        cellRender={MatchGridProfileStatus}
      />
    ),
    [MatchGridColumn.EXPLANATION]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.EXPLANATION}
        name={MatchGridColumn.EXPLANATION}
        displayName="Fit Explanation"
        width={384}
        wrapText
        valueGetter={({ data }) =>
          (data && explanationsById.get(data.id)) ?? NOT_YET
        }
      />
    ),
    [MatchGridColumn.AVAILABILITY]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.AVAILABILITY}
        name={MatchGridColumn.AVAILABILITY}
        displayName="Availability"
        width={128}
        cellRender={MatchGridAvailability}
      />
    ),
    [MatchGridColumn.PROACTIVE]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.PROACTIVE}
        name={MatchGridColumn.PROACTIVE}
        displayName="Open to Proactive Submission"
        width={152}
        wrapHeaderText
        cellRender={({ data }) => (data.proactiveSubmission ? 'Yes' : 'No')}
      />
    ),
    [MatchGridColumn.YOE]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.YOE}
        name={MatchGridColumn.YOE}
        displayName="YOE"
        width={56}
        cellRender={({ data }) => data.yearsOfExperience ?? NOT_YET}
      />
    ),
    [MatchGridColumn.FEEDBACK_SCORE]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.FEEDBACK_SCORE}
        name={MatchGridColumn.FEEDBACK_SCORE}
        displayName="Avg. Feedback Score"
        width={128}
        wrapHeaderText
        cellRender={({ data }) => data.npsScore ?? NOT_YET}
      />
    ),
    [MatchGridColumn.FEEDBACK_RESPONSES]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.FEEDBACK_RESPONSES}
        name={MatchGridColumn.FEEDBACK_RESPONSES}
        displayName="Feedback Responses"
        width={104}
        wrapHeaderText
        cellRender={MatchGridFeedbackResponses}
      />
    ),
    [MatchGridColumn.YEARS_AT_AXIOM]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.YEARS_AT_AXIOM}
        name={MatchGridColumn.YEARS_AT_AXIOM}
        displayName="Years at Axiom"
        width={128}
        cellRender={({ data }) => data.yearsOnAxiomEngagements}
      />
    ),
    [MatchGridColumn.PRICING_RANGE]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.PRICING_RANGE}
        name={MatchGridColumn.PRICING_RANGE}
        displayName="Pricing Range"
        width={144}
        cellRender={({ data }) => MatchGridUtil.formatPricingRange(data)}
      />
    ),
    [MatchGridColumn.TALENT_LOCATION]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.TALENT_LOCATION}
        name={MatchGridColumn.TALENT_LOCATION}
        displayName="Location"
        width={232}
        valueGetter={({ data }) => data?.talentLocation ?? NOT_YET}
      />
    ),
    [MatchGridColumn.BARRED_LOCATION]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.BARRED_LOCATION}
        name={MatchGridColumn.BARRED_LOCATION}
        displayName="Barred Location"
        width={248}
        /*
          A plain value rather than a renderer, so the cell keeps the grid's truncation tooltip -
          which is the only way a row barred in several places shows them all.
        */
        valueGetter={({ data }) => {
          const names = (data?.barredLocationIds ?? []).flatMap(
            id => barredLocationNamesById.get(id) ?? []
          );

          return names.length > 0 ? names.join(', ') : NOT_YET;
        }}
      />
    ),
    [MatchGridColumn.TALENT_PHONE_NUMBER]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.TALENT_PHONE_NUMBER}
        name={MatchGridColumn.TALENT_PHONE_NUMBER}
        displayName="Phone Number"
        width={140}
        wrapHeaderText
        valueGetter={({ data }) => data?.phone ?? NOT_YET}
      />
    ),
    [MatchGridColumn.TALENT_EMAIL]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.TALENT_EMAIL}
        name={MatchGridColumn.TALENT_EMAIL}
        displayName="Email Address"
        width={200}
        cellRender={MatchGridTalentEmail}
      />
    ),
    [MatchGridColumn.MIN_WEEKLY_HOURS_PER_POSITION]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.MIN_WEEKLY_HOURS_PER_POSITION}
        name={MatchGridColumn.MIN_WEEKLY_HOURS_PER_POSITION}
        displayName="Min Weekly Hours"
        width={120}
        wrapHeaderText
        // `||`, not `??`: no minimum is reported as a zero as well as as an absence.
        cellRender={({ data }) => data.minWeeklyHoursPerPosition || NOT_YET}
      />
    ),
    [MatchGridColumn.OWNER_USER]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.OWNER_USER}
        name={MatchGridColumn.OWNER_USER}
        displayName="Talent Success Partner"
        width={264}
        valueGetter={({ data }) => data?.ownerUser ?? NOT_YET}
      />
    ),
    [MatchGridColumn.TALENT_FLAG]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.TALENT_FLAG}
        name={MatchGridColumn.TALENT_FLAG}
        displayName={MatchGridTalentFlagHeader}
        width={144}
        wrapHeaderText
        cellRender={MatchGridTalentFlag}
      />
    ),
    [MatchGridColumn.COMPENSATION]: (
      <DataGridHeader<CandidateDetail>
        key={MatchGridColumn.COMPENSATION}
        name={MatchGridColumn.COMPENSATION}
        displayName="Total Annual Compensation"
        width={208}
        cellRender={({ data }) => MatchGridUtil.formatCompensation(data)}
      />
    ),
  };

  return gridConnector ? (
    <>
      {/*
        Absolute against the column, which is the nearest positioned ancestor - so a save holds the
        whole panel rather than the rows alone, whichever way it was asked for.
      */}
      {isSaving ? (
        <Loader expand="cover" name="MATCH_GRID_SAVING" type="spinner" />
      ) : null}
      <DataGrid
        name={name}
        displayMode="pagination"
        contained
        hidePagination
        lockColumnPinning
        dataSource={gridConnector}
        onColumnOrderChanged={columnNames => {
          void reorderColumns(columnNames);
        }}
        simple
        tooltipShowDelay={1000}
      >
        {orderedColumnIds.flatMap(columnId =>
          isShown(columnId) && configurableHeaders[columnId]
            ? [configurableHeaders[columnId]]
            : []
        )}
        <DataGridHeader<CandidateDetail>
          lockPinned
          suppressMovable
          pinned={isPeeking ? null : 'right'}
          key={MatchGridColumn.BIO}
          name={MatchGridColumn.BIO}
          displayName={
            showColumnConfig
              ? MatchGridHeaderUtil.renderColumnConfig({
                  columns,
                  onClose: () => {
                    setShowSettings(false);
                  },
                  onOpen: () => {
                    setShowSettings(true);
                  },
                  onSave: submitSettings,
                  showModal: showSettings,
                })
              : ''
          }
          width={72}
          cellRender={MatchGridDownloadBio}
        />
      </DataGrid>
    </>
  ) : null;
};
